import { useCallback, useEffect, useRef, useState } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { fetchPaThreadCurrent, streamPaChat } from "@/lib/personalAssistant/paChatClient";
import { FIREFLY_TOUR_CATALOG } from "@/lib/firefly/tourCatalog";
import { FIREFLY_ASK_PILOT_ARTIFACTS } from "@/lib/firefly/pilotAskArtifacts";
import { resolveFireflyDockQuery } from "@/lib/firefly/resolveFireflyDockQuery";
import type { FireflyTourArtifact } from "@/lib/firefly/tourTypes";
import type { PaProposedTool, PaToolId } from "@/lib/personalAssistant/paToolCatalog";
import { parsePaProposedTools } from "@/lib/personalAssistant/paToolCatalog";
import type { PaThreadMessage } from "@/lib/personalAssistant/paThreadTypes";
import { usePaAssistMode } from "@/components/activity/personalAssistant/usePaAssistMode";

export type UsePersonalAssistantThreadArgs = {
  disabled?: boolean;
  currentMode: PrimaryMode | null;
  scopedOrganizationId?: string | null;
  onStartTour?: (artifact: FireflyTourArtifact) => void;
  onNavigateToMode?: (mode: PrimaryMode, highlightTarget?: string) => void;
  onExitPaMode?: () => void;
};

function normalizeToolProposals(raw: unknown): PaProposedTool[] | undefined {
  const parsed = parsePaProposedTools(raw);
  return parsed.length > 0 ? parsed : undefined;
}

function patchMessageProposals(
  messages: PaThreadMessage[],
  assistantMessageId: string,
  proposalId: string,
  status: PaProposedTool["status"],
): PaThreadMessage[] {
  return messages.map((m) => {
    if (m.id !== assistantMessageId || !m.toolProposals?.length) return m;
    return {
      ...m,
      toolProposals: m.toolProposals.map((p) =>
        p.proposalId === proposalId ? { ...p, status } : p,
      ),
    };
  });
}

export function usePersonalAssistantThread({
  disabled = false,
  currentMode,
  scopedOrganizationId = null,
  onStartTour,
  onNavigateToMode,
  onExitPaMode,
}: UsePersonalAssistantThreadArgs) {
  const auth = useAuth();
  const hasCapability = useUserCapabilities();
  const { assistMode } = usePaAssistMode();
  const listRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<PaThreadMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollToBottom = useCallback(() => {
    window.setTimeout(() => {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, 0);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (auth.status !== "ready") return null;
    return (auth.session as unknown as { access_token?: string }).access_token ?? null;
  }, [auth]);

  useEffect(() => {
    if (disabled || auth.status !== "ready" || assistMode !== "personal_assistant") return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const loaded = await fetchPaThreadCurrent({
          token,
          organizationId: scopedOrganizationId ?? auth.activeOrganizationId,
          assistMode,
          impersonationActive: auth.impersonation.active,
        });
        if (!cancelled) {
          setMessages(
            loaded.map((m) => ({
              ...m,
              toolProposals: normalizeToolProposals(m.toolProposals),
            })),
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    assistMode,
    auth.activeOrganizationId,
    auth.impersonation.active,
    auth.status,
    disabled,
    getToken,
    scopedOrganizationId,
  ]);

  const handleProposalResolved = useCallback(
    (assistantMessageId: string, proposalId: string, status: PaProposedTool["status"]) => {
      setMessages((prev) => patchMessageProposals(prev, assistantMessageId, proposalId, status));
    },
    [],
  );

  const handleEditInForm = useCallback(
    (toolId: PaToolId) => {
      onExitPaMode?.();
      if (toolId === "task_create" || toolId === "notification_send" || toolId === "notification_schedule") {
        onNavigateToMode?.("operations");
      }
    },
    [onExitPaMode, onNavigateToMode],
  );

  const submitDraft = useCallback(async () => {
    const text = draft.trim();
    if (!text || disabled || streaming || currentMode == null || auth.status !== "ready") return;

    if (onStartTour) {
      const dock = resolveFireflyDockQuery({
        queryText: text,
        tours: FIREFLY_TOUR_CATALOG,
        asks: FIREFLY_ASK_PILOT_ARTIFACTS,
        auth,
        currentMode,
        canAccessPrimaryMode,
        hasCapability,
      });
      if (dock.route === "tour") {
        onStartTour(dock.tour.artifact);
        setDraft("");
        return;
      }
    }

    setError(null);
    setStreaming(true);
    const optimisticUser: PaThreadMessage = {
      id: `pending-user-${Date.now()}`,
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    };
    const optimisticAssistantId = `pending-assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      optimisticUser,
      { id: optimisticAssistantId, role: "assistant", content: "", createdAt: new Date().toISOString() },
    ]);
    setDraft("");
    scrollToBottom();

    try {
      const token = await getToken();
      if (!token) throw new Error("Sign in required");

      let assistantText = "";
      let proposedTools: PaProposedTool[] | undefined;
      for await (const evt of streamPaChat({
        message: text,
        token,
        organizationId: scopedOrganizationId ?? auth.activeOrganizationId,
        assistMode,
        currentMode,
        impersonationActive: auth.impersonation.active,
      })) {
        if (evt.type === "delta") {
          assistantText += evt.text;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimisticAssistantId ? { ...m, content: assistantText } : m,
            ),
          );
          scrollToBottom();
        } else if (evt.type === "tools") {
          proposedTools = evt.proposedTools;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimisticAssistantId
                ? { ...m, id: evt.assistantMessageId || m.id, toolProposals: evt.proposedTools }
                : m,
            ),
          );
        } else if (evt.type === "done") {
          proposedTools = evt.proposedTools ?? proposedTools;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id === optimisticUser.id) return { ...m, id: evt.userMessageId };
              if (m.id === optimisticAssistantId || m.id === evt.assistantMessageId) {
                return {
                  ...m,
                  id: evt.assistantMessageId,
                  content: assistantText || m.content,
                  toolProposals: proposedTools,
                };
              }
              return m;
            }),
          );
        } else if (evt.type === "error") {
          throw new Error(evt.message);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticAssistantId));
    } finally {
      setStreaming(false);
    }
  }, [
    assistMode,
    auth,
    currentMode,
    disabled,
    draft,
    getToken,
    hasCapability,
    onStartTour,
    scopedOrganizationId,
    scrollToBottom,
    streaming,
  ]);

  return {
    listRef,
    draft,
    setDraft,
    messages,
    streaming,
    error,
    submitDraft,
    assistMode,
    getToken,
    organizationId: scopedOrganizationId ?? auth.activeOrganizationId,
    impersonationActive: auth.impersonation.active,
    handleProposalResolved,
    handleEditInForm,
    onNavigateToMode,
  };
}
